import { CHANGELOG_APPS, CHANGELOG_APP_NAMES, CHANGELOG_DEFAULT_APP, type ChangelogChangeKind, type ChangelogRelease } from "@seagames/contracts";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { ChangelogVisibilityModal } from "../../components/changelog/visibility-modal";
import { usePageBar } from "../../components/shell/page-bar";
import { changelogOptions } from "../../lib/api/changelog";
import { errorMessage } from "../../lib/api/messages";
import { canSeeAllChangelogs, canSeeChangelog } from "../../lib/changelog/access";
import { formatYmd } from "../../lib/format/datetime";

/** Releases per page; "Show older versions" asks for the next. */
const PAGE_SIZE = 10;

export const Route = createFileRoute("/_app/changelog")({
	beforeLoad: ({ context }) => {
		if (!canSeeChangelog(context.session)) throw redirect({ to: "/" });
	},
	validateSearch: z.object({ app: z.enum(CHANGELOG_APPS).optional().catch(undefined) }),
	component: ChangelogPage,
});

const KIND_CHIP: Record<ChangelogChangeKind, string> = {
	NEW: "bg-gms-navy text-white",
	IMPROVED: "bg-gms-soft-2 text-gms-navy-ink",
	FIXED: "bg-gms-warn-soft text-gms-warn",
};

/** A release date is a calendar day (`YYYY-MM-DD`), not a moment: drawn as written, in no time zone. */
const formatDay = (day: string) => formatYmd(day);

/**
 * Change Logs — what changed in each version, newest first, in words a
 * client reads ("Fixed photos not showing"). Everyone the setting allows
 * sees GMS's log; SUPER_ADMIN also picks any other app, and sets who sees
 * the page. The entries are written in the repo with each change; the
 * page only reads them.
 */
function ChangelogPage() {
	const { t } = useTranslation("changelog");
	const { session } = Route.useRouteContext();
	const search = Route.useSearch();
	const navigate = Route.useNavigate();
	const all = canSeeAllChangelogs(session);
	const app = all ? (search.app ?? CHANGELOG_DEFAULT_APP) : CHANGELOG_DEFAULT_APP;
	const [pages, setPages] = useState(1);
	const [settingsOpen, setSettingsOpen] = useState(false);
	const { t: tn } = useTranslation("nav");

	usePageBar({ title: tn("items.changelog"), subtitle: t("page.subtitle") });

	// One request per page; the pages already loaded stay cached, so "Show older" only fetches the next.
	const queries = Array.from({ length: pages }, (_, i) => i + 1);
	const first = useQuery(changelogOptions({ app, page: 1, page_size: PAGE_SIZE }));
	const data = first.data?.app === app ? first.data : undefined;

	const pickApp = (next: (typeof CHANGELOG_APPS)[number]) => {
		setPages(1);
		void navigate({ search: { app: next === CHANGELOG_DEFAULT_APP ? undefined : next }, replace: true });
	};

	return (
		<div className="grid max-w-[52rem] gap-[1.25rem]">
			<div className="flex flex-wrap items-start justify-between gap-[0.75rem]">
				<p className="max-w-[40rem] text-[0.875rem] leading-[1.55] text-gms-ink-2">
					{t("page.intro")}
					{data && <span className="block font-semibold text-gms-navy-ink">{t("page.current", { app: CHANGELOG_APP_NAMES[app], version: data.current_version })}</span>}
				</p>
				{all && (
					<button type="button" className="btn btn-line btn-sm" onClick={() => setSettingsOpen(true)}>
						{t("settings.open")}
					</button>
				)}
			</div>

			{all && data && (
				<div role="tablist" aria-label={t("page.appsLabel")} className="flex flex-wrap gap-[0.375rem]">
					{data.apps.map((a) => (
						<button
							key={a.app}
							type="button"
							role="tab"
							aria-selected={a.app === app}
							className={`flex items-baseline gap-[0.375rem] rounded-full border px-[0.75rem] py-[0.25rem] text-[0.8125rem] ${
								a.app === app ? "border-gms-navy bg-gms-navy text-white" : "border-gms-line bg-base-100 text-gms-navy-ink hover:bg-gms-soft-2"
							}`}
							onClick={() => pickApp(a.app)}
						>
							<span className="font-semibold">{CHANGELOG_APP_NAMES[a.app]}</span>
							<span className={a.app === app ? "text-white/80" : "text-gms-ink-3"}>{t("release.version", { version: a.current_version })}</span>
						</button>
					))}
				</div>
			)}

			{first.isPending ? (
				<span className="loading loading-spinner loading-md text-gms-navy" aria-label={t("page.loading")} />
			) : first.isError && !data ? (
				<div className="grid justify-items-start gap-[0.5rem]">
					<p className="text-[0.875rem] text-error">{errorMessage(first.error)}</p>
					<button type="button" className="btn btn-line btn-sm" onClick={() => void first.refetch()}>
						{t("page.retry")}
					</button>
				</div>
			) : data && data.total === 0 ? (
				<p className="text-[0.875rem] text-gms-ink-3">{t("page.empty")}</p>
			) : (
				<ol className="grid gap-[1rem]">
					{queries.map((page) => (
						<ReleasePage key={`${app}:${page}`} app={app} page={page} />
					))}
				</ol>
			)}

			{data && pages < data.total_pages && (
				<button type="button" className="btn btn-line btn-sm justify-self-start" onClick={() => setPages((p) => p + 1)}>
					{t("page.older")}
				</button>
			)}

			<ChangelogVisibilityModal open={settingsOpen} onClose={() => setSettingsOpen(false)} />
		</div>
	);
}

function ReleasePage({ app, page }: { app: (typeof CHANGELOG_APPS)[number]; page: number }) {
	const q = useQuery(changelogOptions({ app, page, page_size: PAGE_SIZE }));
	if (!q.data) return null;
	return (
		<>
			{q.data.releases.map((r, i) => (
				<Release key={r.version} release={r} latest={page === 1 && i === 0} />
			))}
		</>
	);
}

function Release({ release, latest }: { release: ChangelogRelease; latest: boolean }) {
	const { t } = useTranslation("changelog");
	return (
		<li className="grid gap-[0.625rem] rounded-box border border-gms-line bg-base-100 px-[1.125rem] py-[1rem]">
			<div className="flex flex-wrap items-baseline gap-x-[0.75rem] gap-y-[0.25rem]">
				<h2 className="text-[1.125rem] font-bold text-gms-navy-ink">{t("release.version", { version: release.version })}</h2>
				<span className="text-[0.8125rem] text-gms-ink-3">{formatDay(release.date)}</span>
				{latest && (
					<span className="rounded-full bg-gms-navy px-[0.5rem] py-[0.0625rem] text-[0.71875rem] font-semibold text-white">{t("release.latest")}</span>
				)}
			</div>
			<ul className="grid gap-[0.5rem]">
				{release.changes.map((c, i) => (
					<li key={i} className="flex items-start gap-[0.625rem] text-[0.875rem] leading-[1.5] text-gms-navy-ink">
						<span className={`mt-[0.125rem] min-w-[5.5rem] shrink-0 rounded-full px-[0.5rem] py-[0.0625rem] text-center text-[0.71875rem] font-semibold ${KIND_CHIP[c.kind]}`}>
							{t(`kind.${c.kind}`)}
						</span>
						<span>{c.text}</span>
					</li>
				))}
			</ul>
		</li>
	);
}
